import { sInputKeyValidationHelper, sAddCommaToNumber } from "../../shared.min.js";
import { RATES } from './stamp-duty-data.min.js';

const _stampDuty = (function () {

    const CALCULATION_MIN_PRICE = 0; // Minimum price to trigger calculation
    const domElements = {};
    let linksArr = [];

    const buyerTypeArr = ['first_time', 'main', 'additional'];
    const countryArr = ['england_ni', 'scotland', 'wales'];

    const init = () => {
        domElements.widget = document.querySelector('.calculator--stamp-duty');
        if (!domElements.widget) return;

        const { widget } = domElements;

        domElements.priceInput = widget.querySelector('#property-price');
        domElements.buyerTypeInput = widget.querySelectorAll('input[name="property-type"]');
        domElements.countryInput = widget.querySelectorAll('input[name="property-location"]');
        domElements.totalCostElement = widget.querySelector('.total-cost__figure');
        domElements.footerAmount = widget.querySelector('.footer-amount');
        domElements.resultsLink = widget.querySelector('#summaryCTA');
        domElements.englandAndNiInfoText = widget.querySelector('.england_ni_info_text');
        domElements.scotlandInfoText = widget.querySelector('.scotland_info_text');
        domElements.walesInfoText = widget.querySelector('.wales_info_text');
        domElements.summaryBreakdown = widget.querySelector('.summary__breakdown');
        domElements.template = document.getElementById('band-info-row-template');
        domElements.infoRowHeader = widget.querySelector('.info-row--header');

        linksArr = [widget.getAttribute('data-first-time-buyer-link'), widget.getAttribute('data-next-home-buyer-link'), widget.getAttribute('data-additional-property-buyer-link')];

        if (!domElements.priceInput || domElements.buyerTypeInput.length === 0 || domElements.countryInput.length === 0) return;

        const { priceInput, buyerTypeInput, countryInput } = domElements;

        priceInput.addEventListener('input', () => {
            onChangeHandler();
            priceInput.value = sAddCommaToNumber(priceInput.value); // Remove commas for processing
        });
        sInputKeyValidationHelper(priceInput, 'numeric');

        buyerTypeInput.forEach(input => input.addEventListener('change', () => {
            onChangeHandler();
            updateLinkHref();
        }));

        countryInput.forEach(input => input.addEventListener('change', () => {
            onChangeHandler();
            updateInfoText();
        }));
    };

    const onChangeHandler = () => {
        const { priceInput, buyerTypeInput, countryInput } = domElements;
        const priceInputValue = parseInt(priceInput.value.replace(/,/g, '')) || 0;
        const buyerTypeInputValue = Array.from(buyerTypeInput).find(input => input.checked)?.value;
        const countryInputValue = Array.from(countryInput).find(input => input.checked)?.value;

        if (!countryInputValue || !buyerTypeInputValue || priceInputValue <= CALCULATION_MIN_PRICE) return;

        const rates = getRatesAndCalculateStampDuty(priceInputValue, countryInputValue, buyerTypeInputValue);
        if (!priceInputValue || !rates) return;
        
        const { total, breakdown } = calculateStampDuty(priceInputValue, rates);

        updateUIWithResults(total, breakdown);
    };

    const getRatesAndCalculateStampDuty = (priceInputValue, countryInputValue, buyerTypeInputValue) => {
        let rates = RATES[countryArr[countryInputValue]];
        const buyerType = buyerTypeArr[buyerTypeInputValue];

        rates = rates[buyerType];

        // There is a rule for relief on first time buyers so enter if block
        if (rates.rule) {
            const maxPriceForRelief = rates.rule.maxPrice;
            
            if (priceInputValue > maxPriceForRelief) {
                // House price exceeds the maximum for first-time buyer relief; use fallback rates that are defined in the data
                const fallback = rates.rule.fallback;
                rates = RATES[countryArr[countryInputValue]][fallback];
            } else {
                rates = rates.bands;
            }
            return rates;
        }

        return rates;
    };

    const calculateStampDuty = (price, rates) => {
        let remaining = price;
        let previousLimit = 0;
        let total = 0;
        const breakdown = [];
        
        for (const band of rates) {
            const { upTo, rate } = band;
        
            const taxable =
            upTo === null
                ? remaining // final band (no upper limit)
                : Math.max(0, Math.min(upTo - previousLimit, remaining));
        
            const tax = taxable * rate;
        
            if (taxable > 0) {
            breakdown.push({
                from: previousLimit,
                to: upTo,
                rate,
                taxable,
                tax,
            });
            }
        
            total += tax;
            remaining -= taxable;
            previousLimit = upTo;
        
            if (remaining <= 0) break;
        }
        
        return {
            total: Number(total.toFixed(2)),
            breakdown,
        };
    };

    const updateUIWithResults = (amount, breakdown) => {
        const { totalCostElement, footerAmount, template, infoRowHeader, summaryBreakdown } = domElements;
        const amountFormatted = `£${sAddCommaToNumber(Math.round(amount))}`;

        totalCostElement.textContent = amountFormatted;
        footerAmount.textContent = amountFormatted;

        document.querySelectorAll('.info-row').forEach(el => {
            if (!el.classList.contains('info-row--header')) {
              el.remove();
            }
        });

        let elementToAppendAfter = infoRowHeader;
        for (const band of breakdown.reverse()) {
            const clone = template.content.cloneNode(true);
            const infoBandElement = clone.querySelector('.info-band');
            const infoRateElement = clone.querySelector('.info-rate');
            const infoTaxElement = clone.querySelector('.info-tax');

            if (infoBandElement) {
                infoBandElement.textContent = `£${band.from}  ${band.to ? "- " + "£" + sAddCommaToNumber(band.to) : '+'}`;
            }

            if (infoRateElement) {
                const rate = band.rate * 100;
                const formattedRate = (rate % 1 === 0) ? rate.toFixed(0) : rate.toFixed(1);
                infoRateElement.textContent = `${formattedRate}%`;
            }

            if (infoTaxElement) {
                infoTaxElement.textContent = `£${sAddCommaToNumber(band.tax.toFixed(2))}`;
            }

            elementToAppendAfter.parentNode.insertBefore(clone, elementToAppendAfter.nextSibling);
            elementToAppendAfter = elementToAppendAfter.nextSibling;
        }

        summaryBreakdown.classList.remove('display-none');
    }

    const updateLinkHref = () => {
        if (!linksArr.length) return;
        const { buyerTypeInput, resultsLink } = domElements;
        const link = linksArr[Array.from(buyerTypeInput).find(input => input.checked)?.value];
        resultsLink.href = link;
    }

    const updateInfoText = () => {
        const { countryInput, englandAndNiInfoText, scotlandInfoText, walesInfoText } = domElements;
        const countryInputValue = Array.from(countryInput).find(input => input.checked)?.value;
        const country = countryArr[countryInputValue];

        [englandAndNiInfoText, scotlandInfoText, walesInfoText].forEach(el => el.classList.add('display-none'));
        if (country === 'england_ni') englandAndNiInfoText.classList.remove('display-none');
        if (country === 'scotland') scotlandInfoText.classList.remove('display-none');
        if (country === 'wales') walesInfoText.classList.remove('display-none');
    };

    return {
        init
    };

})();

document.addEventListener('DOMContentLoaded', () => _stampDuty.init());